import { useState } from 'react';

import { useAppDispatch, useAppSelector } from "@/OneLiners";

import {  ActionResponse, getFingerprint, ResponseHook, sessionStateActions } from '@/Shared';
import { AuthService } from '../AuthService';
import { LoginValues } from "../LoginFormValidation";
import { useSession } from './useSession';


/**
 * =====================================================
 * Hook: useAuth
 * =====================================================
 *
 * Description:
 * Custom hook that provides authentication logic for the application.
 * Manages login, logout, and visitor session creation.
 *
 * Responsibilities:
 * - Handle user login and update global session state
 * - Handle user logout and clear session
 * - Generate visitor fingerprint and retrieve an enigma challenge
 * - Provide reactive response states for login, logout, and visitor creation
 *
 * Returns:
 * - handleLogIn(values): logs in a user and updates session state
 * - handleLogOut(): logs out the current user and clears session
 * - createVisitor(): generates visitor fingerprint and retrieves enigma challenge
 * - visitorResponse: ActionResponse of visitor creation
 * - logInResponse: ActionResponse of login
 * - logOutResponse: ActionResponse of logout
 * - userSession: current session state from Redux
 *
 * Notes:
 * - ResponseHook helpers manage loading, success, and error states
 * - initSession and clearSession from useSession handle additional session setup/cleanup
 *
 * Author: Samano CASTRE
 * Date: January 31, 2026
 */
export const useAuth = () => {


	/** =====================================================
     * Redux & Local State
     * =====================================================
     *
     * Retrieves current user session from Redux and manages
     * ActionResponse states for login, logout, and visitor creation.
     */
	const dispatch = useAppDispatch();

	const  userSession  = useAppSelector(state => state.sessionState.userSession);
	const [logInResponse, setLogInResponse] = useState<ActionResponse>({ status: 'idle' });
	const [logOutResponse, setLogOutResponse] = useState<ActionResponse>({ status: 'idle' });
	const [visitorResponse, setVisitorResponse] = useState<ActionResponse>({ status: 'idle' });
	const { clearSession, initSession } = useSession();

	/** =====================================================
     * Handle User Login
     * =====================================================
     *
     * Performs login using AuthService and updates global
     * session state. Manages loading, success, and error states.
     *
     * @param values LoginValues object containing user credentials
     */
	const handleLogIn = (values: LoginValues) => {
		ResponseHook.loading(setLogInResponse);
		AuthService.logIn(values).then(async data => {
			const userSession = data.data;

			// Update state to success
			ResponseHook.success(setLogInResponse);

			// Persist session in Redux
			dispatch(sessionStateActions.setUserSession(userSession));

			// Perform any additional session initialization
			await initSession();
		}).catch(error => ResponseHook.error(setLogInResponse, error, "Erreur lors de l'authentification."));
	};


	 /** =====================================================
     * Handle User Logout
     * =====================================================
     *
     * Performs logout using AuthService and clears global
     * session state. Manages loading, success, and error states.
     */
	const handleLogOut = () => {
		ResponseHook.loading(setLogOutResponse);
		AuthService.logOut().then(async data => {

			// Clear session state and other session data
			await clearSession();

			// Update state to success
			ResponseHook.success(setLogOutResponse);
			
		}).catch(error => ResponseHook.error(setLogOutResponse, error, "Erreur lors de la déconnexion"));
	}

	/** =====================================================
     * Create Visitor Session
     * =====================================================
     *
     * Generates a visitor fingerprint and retrieves an enigma
     * challenge from AuthService.
     *
     * Updates Redux with the visitor challenge and manages
     * loading, success, and error states.
     */
	const createVisitor = async () => {
		ResponseHook.loading(setVisitorResponse);
		try {
			// Generate unique visitor fingerprint
			const visitor = await getFingerprint();

			// Retrieve challenge from server
			const { data } = await AuthService.getVisitorEnigma(visitor);

			// Store challenge in Redux
			dispatch(sessionStateActions.setUserChallenge(data));

			// Update state to success
			ResponseHook.success(setVisitorResponse);

			return data;
		} catch (error: any) {
			ResponseHook.error(setVisitorResponse, error, "Erreur lors de la récupération de l'énigme");
			return null;
		}
	};

	/** =====================================================
     * Return API
     * =====================================================
     *
     * Exposes login, logout, visitor creation functions,
     * their respective response states, and current session.
     */
	return {
		handleLogIn,
		handleLogOut,
		createVisitor,
		visitorResponse,
		logInResponse,
		logOutResponse,
		userSession
	};
};